/**
 * Environment Card Component
 *
 * Displays a single environment in the library grid. The first reference
 * image is used as the thumbnail.
 */

import React from 'react';
import { Environment } from '../../types/EnvironmentTypes';
import { useCharacterImageUrl } from '../../services/characterStorageService';
import { Edit, Delete } from '@mui/icons-material';
import './EnvironmentLibrary.css';

interface EnvironmentCardProps {
  environment: Environment;
  /** Opens the environment for viewing/editing. */
  onOpen: () => void;
  onDelete: () => void;
  /** Optional "use in video" action (e.g. from pickers). */
  onSelect?: () => void;
  selected?: boolean;
}

const EnvironmentCard: React.FC<EnvironmentCardProps> = ({
  environment,
  onOpen,
  onDelete,
  onSelect,
  selected,
}) => {
  const primaryImage = environment.referenceImages[0];
  // Stored urls may be relative blob paths in the user's storage directory;
  // sign them with the user SAS token for display.
  const imageSrc = useCharacterImageUrl(primaryImage?.url);
  const referenceCount = environment.referenceImages.length;

  const displayName =
    environment.name.length > 24
      ? `${environment.name.substring(0, 21)}...`
      : environment.name;

  return (
    <div className={`environment-card${selected ? ' selected' : ''}`}>
      {/* Image Preview (first reference image as thumbnail) */}
      <div
        className="environment-card-image"
        onClick={onOpen}
        title={`Open ${environment.name}`}
        style={{ cursor: 'pointer' }}
      >
        {imageSrc ? (
          <img src={imageSrc} alt={environment.name} />
        ) : (
          <div className="environment-card-image-placeholder">
            No preview
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="environment-card-info">
        <div className="environment-card-name" title={environment.name}>
          {displayName}
        </div>

        {environment.description && (
          <div
            className="environment-card-description"
            title={environment.description}
          >
            {environment.description}
          </div>
        )}

        <div className="environment-card-meta">
          {referenceCount} reference image{referenceCount === 1 ? '' : 's'}
        </div>

        {/* Action Buttons */}
        <div className="environment-card-actions">
          {onSelect && (
            <button
              className="card-action-btn primary"
              onClick={onSelect}
              title={`Use ${environment.name} in video generation`}
            >
              Use
            </button>
          )}
          <button
            className="card-action-btn secondary"
            onClick={onOpen}
            title={`Edit ${environment.name}`}
          >
            <Edit style={{ fontSize: 14 }} />
            Edit
          </button>
          <button
            className="card-action-btn danger"
            onClick={onDelete}
            title="Delete"
          >
            <Delete style={{ fontSize: 14 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentCard;
